import React, {FC, useEffect, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import {useAuth} from '../../auth'
import type {MainEngineTrialAttachmentDto, MainEngineTrialType} from '../core/_models'
import {
  deleteMainEngineTrialAttachment,
  listMainEngineTrialAttachments,
  mainEngineTrialAttachmentDownloadUrl,
  mainEngineTrialAttachmentViewUrl,
  uploadMainEngineTrialAttachment,
} from '../core/_requests'

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

type Props = {
  visible: boolean
  trialType: MainEngineTrialType | null
  vesselId: number | null
  vesselName?: string
  canEdit: boolean
  canUpload?: boolean
  onClose: () => void
}

const formatBytes = (n?: number) => {
  const v = Number(n ?? 0)
  if (!Number.isFinite(v) || v <= 0) return '-'
  const units = ['B', 'KB', 'MB', 'GB']
  let size = v
  let i = 0
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024
    i++
  }
  const digits = i === 0 ? 0 : 1
  return `${size.toFixed(digits)} ${units[i]}`
}

const titleFor = (t: MainEngineTrialType | null) =>
  t === 'SEATRIAL' ? 'Seatrial Report' : t === 'SHOPTRIAL' ? 'Shoptrial Report' : 'Trial Report'

const MainEngineTrialAttachmentsModal: FC<Props> = ({
  visible,
  trialType,
  vesselId,
  vesselName,
  canEdit,
  canUpload = canEdit,
  onClose,
}) => {
  const {auth} = useAuth()
  const token = auth?.auth?.jwt

  const [items, setItems] = useState<MainEngineTrialAttachmentDto[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const load = async () => {
    if (!visible || !vesselId || !trialType) return
    try {
      setLoading(true)
      const data = await listMainEngineTrialAttachments(vesselId, trialType)
      setItems(data)
    } catch (err: any) {
      toast.error('Failed to load attachments')
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, vesselId, trialType])

  if (!visible) return null

  const handleSelectFile = () => fileInputRef.current?.click()

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !vesselId || !trialType) return
    try {
      setUploading(true)
      await uploadMainEngineTrialAttachment(vesselId, trialType, file)
      toast.success('Attachment uploaded')
      await load()
    } catch (err: any) {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async (item: MainEngineTrialAttachmentDto) => {
    try {
      setDownloadingId(item.id)
      const res = await fetch(mainEngineTrialAttachmentDownloadUrl(item.id), {
        headers: token ? {Authorization: `Bearer ${token}`} : undefined,
      })
      if (!res.ok) throw new Error(`Download failed: ${res.status}`)
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = item.fileName || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
    } catch (err: any) {
      toast.error('Download failed')
    } finally {
      setDownloadingId(null)
    }
  }

  const handleView = async (item: MainEngineTrialAttachmentDto) => {
    try {
      const res = await fetch(mainEngineTrialAttachmentViewUrl(item.id), {
        headers: token ? {Authorization: `Bearer ${token}`} : undefined,
      })
      if (!res.ok) throw new Error(`View failed: ${res.status}`)
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      window.open(objectUrl, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    } catch (err: any) {
      toast.error('View failed')
    }
  }

  const handleRemove = async (id: number) => {
    if (!window.confirm('Remove this attachment?')) return
    try {
      setDeletingId(id)
      await deleteMainEngineTrialAttachment(id)
      toast.success('Attachment removed')
      await load()
    } catch (err: any) {
      toast.error('Failed to remove')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div style={WRAP_STYLE} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='bg-white rounded shadow-lg d-flex flex-column'
        style={{width: '100%', maxWidth: 850, maxHeight: '90vh'}}
      >
        <div className='d-flex justify-content-between align-items-center border-bottom px-5 py-4'>
          <div>
            <h5 className='modal-title mb-0'>
              {titleFor(trialType)} Attachments
              {vesselName ? <span className='text-muted fs-7 ms-2'>– {vesselName}</span> : null}
            </h5>
            <span className='text-muted fs-8'>Per-vessel, one-time reference attachment</span>
          </div>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>

        {canUpload && (
          <div className='d-flex justify-content-end px-5 pt-4'>
            <input
              ref={fileInputRef}
              type='file'
              className='d-none'
              onChange={handleUpload}
            />
            <button
              type='button'
              className='btn btn-sm btn_primary'
              onClick={handleSelectFile}
              disabled={uploading || !vesselId}
            >
              <i className='bi bi-upload me-1' />
              {uploading ? 'Uploading...' : 'Upload New'}
            </button>
          </div>
        )}

        <div className='p-5 text-dark flex-grow-1' style={{overflowY: 'auto'}}>
          {loading ? (
            <div className='text-muted text-center py-4'>Loading...</div>
          ) : items.length === 0 ? (
            <div className='text-muted text-center py-4'>No attachments uploaded yet.</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-sm align-middle mb-0'>
                <thead>
                  <tr className='bg-light-primary'>
                    <th style={{width: 70}}>Sr/No</th>
                    <th>File Name</th>
                    <th style={{width: 180}}>Uploaded By</th>
                    <th style={{width: 120}}>Size</th>
                    <th className='text-end pe-6' style={{width: 240}}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((att, idx) => (
                    <tr key={att.id}>
                      <td>{idx + 1}</td>
                      <td>
                        <div
                          className='text-truncate'
                          style={{maxWidth: 320}}
                          title={att.fileName}
                        >
                          {att.fileName}
                        </div>
                      </td>
                      <td>{att.uploadedByName || '-'}</td>
                      <td>{formatBytes(att.fileSize)}</td>
                      <td>
                        <div className='d-flex justify-content-end align-items-center gap-2 flex-nowrap'>
                          <button
                            type='button'
                            className='btn btn-sm btn-light-primary'
                            onClick={() => handleView(att)}
                          >
                            View
                          </button>
                          <button
                            type='button'
                            className='btn btn-sm btn-light-info'
                            onClick={() => handleDownload(att)}
                            disabled={downloadingId === att.id}
                          >
                            {downloadingId === att.id ? 'Downloading...' : 'Download'}
                          </button>
                          {canEdit && (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-danger'
                              onClick={() => handleRemove(att.id)}
                              disabled={deletingId === att.id}
                            >
                              {deletingId === att.id ? 'Removing...' : 'Remove'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className='d-flex justify-content-end align-items-center border-top px-5 py-4'>
          <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default MainEngineTrialAttachmentsModal
